import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  CompanyContract,
  CompanyContractUpsertPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Sub-recurso de contrato de empresa.
 *
 * Endpoints:
 *   - GET `/api/structure/companies/{companyId}/contracts`
 *   - PUT `/api/structure/companies/{companyId}/contracts` (multipart/form-data)
 *
 * El PUT espera claves repetidas `files` + `orders` en paralelo (una por
 * archivo), no la notación Spring `files[i]`. El GET puede devolver `data`
 * vacío u omitir campos cuando aún no hay contrato.
 */
@Injectable({ providedIn: 'root' })
export class CompanyContractService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  get(companyId: string): Observable<CompanyContract | null> {
    return this.httpClient
      .get<ApiResponse<CompanyContract | null>>(
        `${this.apiUrl}/${companyId}/contracts`,
      )
      .pipe(map((response) => response.data ?? null));
  }

  update(
    companyId: string,
    payload: CompanyContractUpsertPayload,
  ): Observable<CompanyContract | null> {
    return this.httpClient
      .put<ApiResponse<CompanyContract | null>>(
        `${this.apiUrl}/${companyId}/contracts`,
        this.toFormData(payload),
      )
      .pipe(map((response) => response.data ?? null));
  }

  /**
   * Multipart alineado al contrato Postman:
   *   mandatory, number, startDate, endDate, observations,
   *   files (File, repetido), orders (text, repetido en paralelo).
   */
  private toFormData(payload: CompanyContractUpsertPayload): FormData {
    const formData = new FormData();

    formData.append('mandatory', String(payload.mandatory));

    if (payload.number != null && payload.number !== '') {
      formData.append('number', payload.number);
    }
    if (payload.startDate) {
      formData.append('startDate', payload.startDate);
    }
    if (payload.endDate) {
      formData.append('endDate', payload.endDate);
    }
    if (payload.observations != null && payload.observations !== '') {
      formData.append('observations', payload.observations);
    }

    const files: File[] = payload.files ?? [];
    const orders: number[] = payload.orders ?? [];

    files.forEach((file: File, index: number) => {
      formData.append('files', file);
      const order = orders[index] ?? index + 1;
      formData.append('orders', String(order));
    });

    return formData;
  }
}
